import {Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router, RouterLink, RouterOutlet} from '@angular/router';
import {interval} from 'rxjs';
import {MatBadgeModule} from '@angular/material/badge';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ROLES_CHANGED_NOTICE_KEY} from './auth/auth.interceptor';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatToolbarModule} from '@angular/material/toolbar';
import {AppUpdateService} from './app-update.service';
import {AuthService} from './auth/auth.service';
import {DASHBOARD_ROLES} from './dashboard/dashboard-config';
import {highestRoleIcon} from './user/role-icon';
import {LoginDialogComponent} from './auth/login-dialog/login-dialog.component';
import {OrderDialogService} from './order/order-dialog.service';
import {ManageServiceConfigurationsDialogComponent}
  from './service-configuration/manage-service-configurations-dialog/manage-service-configurations-dialog.component';
import {ServiceConfigurationService} from './service-configuration/service-configuration.service';
import {VersionService} from './version.service';
import {environment} from '../environments/environment';
import {NewMessageAlertService} from './message/new-message-alert.service';
import {PushService} from './push/push.service';

/** Used only if the version endpoint (which normally carries the configured interval) can't be reached. */
const DEFAULT_UPDATE_CHECK_INTERVAL_MINUTES = 5;

/** Keeps list-style dialogs anchored near the top instead of Material's default vertical centering, which
 * on a tall table shoves the header off-screen and looks like the dialog is "floating" mid-page. */
const TOP_DIALOG_POSITION = {top: '80px'};

const ADMIN_DIALOG_CONFIG: MatDialogConfig = {
  width: '90vw',
  maxWidth: '95vw',
  disableClose: true,
  position: TOP_DIALOG_POSITION
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, MatToolbarModule, MatButtonModule, MatMenuModule, MatIconModule,
    MatTooltipModule, MatDividerModule, MatBadgeModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly newMessageAlerts = inject(NewMessageAlertService);
  private readonly pushService = inject(PushService);
  /** Unread messages (own log + staff queue), for the badge on the message buttons. */
  readonly unreadCount = this.newMessageAlerts.unreadCount;
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly orderDialogService = inject(OrderDialogService);
  private readonly serviceConfigurationService = inject(ServiceConfigurationService);
  private readonly versionService = inject(VersionService);
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly destroyRef = inject(DestroyRef);

  private sessionExpiredDialogOpen = false;
  private updateAvailableShown = false;
  private mfaReminderShown = false;

  title = 'Christbaumaktion';
  cbActionYear: number | null = null;
  /** Fails open (true) on error/before the response arrives, since this is purely presentational - the
   * backend enforces the actual login gate regardless of what the toolbar shows (see AuthController). */
  customerServiceEnabled = true;
  readonly appVersion = environment.version;
  backendVersion: string | null = null;

  /** After the reload the auth interceptor triggers when the user's roles changed (see authInterceptor). */
  private showRolesChangedNotice(): void {
    try {
      if (sessionStorage.getItem(ROLES_CHANGED_NOTICE_KEY)) {
        sessionStorage.removeItem(ROLES_CHANGED_NOTICE_KEY);
        this.snackBar.open('Deine Berechtigungen wurden geändert. Die Ansicht wurde neu aufgebaut.', 'OK',
          {duration: 8000});
      }
    } catch {
      // sessionStorage unavailable: no notice
    }
  }

  constructor() {
    this.showRolesChangedNotice();
    this.setupMessageDeepLinks();
    if (this.isLoggedIn) {
      this.remindAboutMfaSetup();
    }
    this.authService.loggedIn$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.remindAboutMfaSetup());
    this.authService.sessionExpired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleSessionExpired());

    this.serviceConfigurationService.getCbActionYear().subscribe({
      next: response => this.cbActionYear = response.year,
      error: () => this.cbActionYear = null
    });

    this.serviceConfigurationService.getCustomerServiceStatus().subscribe({
      next: response => this.customerServiceEnabled = response.enabled,
      error: () => this.customerServiceEnabled = true
    });

    this.versionService.get().subscribe({
      next: response => {
        this.backendVersion = response.version;
        this.setupUpdateCheck(response.updateCheckIntervalMinutes);
      },
      error: () => {
        this.backendVersion = null;
        this.setupUpdateCheck(DEFAULT_UPDATE_CHECK_INTERVAL_MINUTES);
      }
    });
  }

  private setupUpdateCheck(intervalMinutes: number): void {
    this.appUpdateService.init().then(() => {
      interval(intervalMinutes * 60 * 1000)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.checkForUpdate());

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          this.checkForUpdate();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisible));
    });
  }

  private checkForUpdate(): void {
    this.refreshCustomerServiceStatus();
    if (this.updateAvailableShown) {
      return;
    }
    this.appUpdateService.hasUpdate().then(hasUpdate => {
      if (!hasUpdate) {
        return;
      }
      this.updateAvailableShown = true;
      const snackBarRef = this.snackBar.open('Eine neue Version ist verfügbar.', 'Neu laden');
      snackBarRef.onAction().subscribe(() => window.location.reload());
    });
  }

  /** Keeps the red "customer service off" header/footer current for staff who didn't toggle it themselves. */
  private refreshCustomerServiceStatus(): void {
    if (!this.isStaff) {
      return;
    }
    this.serviceConfigurationService.getCustomerServiceStatus().subscribe({
      next: response => this.customerServiceEnabled = response.enabled
    });
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  /** Same staff-role set as the backend's OrderController/StaffMessageController. */
  get isStaff(): boolean {
    return ['DRIVER', 'COLLECTOR', 'OFFICER', 'PLANNER', 'CONTROLLER', 'MANAGER', 'ADMIN']
      .some(role => this.authService.hasRole(role));
  }

  /** A pure TEAMBOARD account (display terminal) only gets the logout button in the toolbar. */
  get isTeamboardOnly(): boolean {
    const user = this.authService.getCurrentUser();
    return !!user && user.roles.length > 0 && user.roles.every(role => role === 'TEAMBOARD');
  }

  /** A pure CUSTOMER account (no other role) - gets no push notifications, see PushController. */
  get isCustomerOnly(): boolean {
    const user = this.authService.getCurrentUser();
    return !!user && user.roles.length > 0 && user.roles.every(role => role === 'CUSTOMER');
  }

  /** Menu shortcut to the dashboard picker - only useful for users holding more than one dashboard role. */
  get hasMultipleDashboardRoles(): boolean {
    const user = this.authService.getCurrentUser();
    return !!user && DASHBOARD_ROLES.filter(role => user.roles.includes(role)).length > 1;
  }

  get canManagePickupDates(): boolean {
    return this.isAdmin || this.authService.hasRole('MANAGER');
  }

  /** Icon-Zuordnung (uMap pictogram -> own icon): same roles as the backend's RouteIconMappingController. */
  get canManageRouteIcons(): boolean {
    return this.isAdmin || this.authService.hasRole('MANAGER') || this.authService.hasRole('PLANNER');
  }

  get canManageServiceConfigurations(): boolean {
    return this.isAdmin || this.authService.hasRole('MANAGER');
  }

  /** Userverwaltung: ADMIN fully; PLANNER/MANAGER may open it too but only maintain the crew roles (MANAGER
   * additionally enable/disable) - see UserController.updateUser(), which enforces that. */
  get canViewUsers(): boolean {
    return this.isAdmin || this.authService.hasRole('MANAGER') || this.authService.hasRole('PLANNER');
  }

  /** Whether the menu has entries besides Userverwaltung (decides on the divider below it). */
  get hasOtherAdminMenuEntries(): boolean {
    return this.isAdmin || this.canManagePickupDates || this.canManageServiceConfigurations;
  }

  /** Gates the Admin menu trigger itself: visible if the user is allowed to use at least one entry inside it. */
  get showAdminMenu(): boolean {
    return this.canViewUsers || this.hasOtherAdminMenuEntries;
  }

  /** "Meine Aufträge" is redundant once a pure CUSTOMER lands on the Kundendashboard (issue #37) as their home
   * page, so it's hidden for them; kept for CREATOR-only accounts for now (still under discussion). Staff always
   * keep it regardless of also holding CUSTOMER - they never land on the Kundendashboard (see myDashboardRoles
   * in LandingPageComponent, which only looks at staff roles), so this stays their way to reach the order list,
   * same as before this change. */
  get showMyOrdersMenuItem(): boolean {
    return this.isStaff || !this.authService.hasRole('CUSTOMER');
  }

  /** Roles for which the second factor is (going to be) mandatory - same set as User.isMfaRequiredByRole(). */
  get canUseMfa(): boolean {
    return ['ADMIN', 'MANAGER', 'OFFICER', 'PLANNER', 'CONTROLLER'].some(role => this.authService.hasRole(role));
  }

  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  /** Red header/footer so logged-in staff notice the customer area is currently locked (customerService = false). */
  get showCustomerServiceDisabledWarning(): boolean {
    return !this.customerServiceEnabled && this.isStaff;
  }

  get currentUserName(): string {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return '';
    }
    const name = [user.lastName, user.firstName].filter(Boolean).join(', ');
    return name || user.email;
  }

  /** Only set when a name is shown, so the email can be hidden separately on narrow screens without losing it entirely. */
  get currentUserEmail(): string | null {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return null;
    }
    const name = [user.lastName, user.firstName].filter(Boolean).join(', ');
    return name ? user.email : null;
  }

  get copyrightTooltip(): string {
    const lines = [`Frontend Version: ${this.appVersion}`];
    if (this.backendVersion) {
      lines.push(`Backend Version: ${this.backendVersion}`);
    }
    return lines.join('\n');
  }

  get currentUserRolesTooltip(): string {
    return this.authService.getCurrentUser()?.roles.join('\n') ?? '';
  }

  /** Issue #88: footer shows the icon of the user's highest-ranked role. */
  get currentUserRoleIcon(): string | null {
    const user = this.authService.getCurrentUser();
    return user ? highestRoleIcon(user.roles) : null;
  }

  openOrderDialog(): void {
    this.orderDialogService.open();
  }

  /** Any logged-in user (not just staff) has their own orders, so this is loaded on demand rather than eager. */
  openMyOrdersDialog(): void {
    import('./order/my-orders-dialog/my-orders-dialog.component').then(({MyOrdersDialogComponent}) => {
      this.dialog.open(MyOrdersDialogComponent, {
        width: '90vw',
        maxWidth: '95vw',
        position: TOP_DIALOG_POSITION
      });
    });
  }

  openLoginDialog(): void {
    const dialogRef = this.dialog.open(LoginDialogComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(loggedIn => {
      if (loggedIn) {
        this.snackBar.open('Logged in successfully.', 'Ok', {duration: 4000});
        this.navigateHomeIfSelfService('login');
      }
    });
  }

  /**
   * CUSTOMER accounts always land on their Kundendashboard (issue #37, rendered by LandingPageComponent for
   * anyone with no staff role) right after logging in - including re-login after a session expiry mid-use.
   * CREATOR-only accounts (self-service, but usually mid-task recording someone else's order over the phone)
   * only get this on the initial login, not when re-authenticating after an expiry interrupts whatever they
   * were doing. Staff accounts never get this.
   */
  private navigateHomeIfSelfService(context: 'login' | 'session-expired'): void {
    const user = this.authService.getCurrentUser();
    if (!user) {
      return;
    }
    const roles = user.roles;
    const isSelfServiceOnly = roles.length > 0 && roles.every(role => role === 'CUSTOMER' || role === 'CREATOR');
    if (!isSelfServiceOnly) {
      return;
    }
    if (roles.includes('CUSTOMER') || context === 'login') {
      this.router.navigate(['/']);
    }
  }

  openManageServiceConfigurationsDialog(): void {
    this.dialog.open(ManageServiceConfigurationsDialogComponent, ADMIN_DIALOG_CONFIG).afterClosed().subscribe(() => {
      // customerService may have been toggled: reload so the red header/footer and landing page reflect it
      this.serviceConfigurationService.getCustomerServiceStatus().subscribe({
        next: response => {
          if (response.enabled !== this.customerServiceEnabled) {
            window.location.reload();
          }
        }
      });
    });
  }

  openRouteIconMappingsDialog(selectId?: string): void {
    import('./pickup-tour/route-icon-mapping/route-icon-mappings-dialog/route-icon-mappings-dialog.component')
      .then(({ RouteIconMappingsDialogComponent }) => {
        this.dialog.open(RouteIconMappingsDialogComponent, { ...ADMIN_DIALOG_CONFIG, data: { selectId } });
      });
  }

  openStaffMessagesDialog(): void {
    import('./message/staff-messages-dialog/staff-messages-dialog.component').then(({ StaffMessagesDialogComponent }) => {
      this.dialog.open(StaffMessagesDialogComponent, {
        width: '66vw',
        maxWidth: '95vw',
        panelClass: 'mobile-fullscreen-dialog',
        position: TOP_DIALOG_POSITION
      });
    });
  }

  logout(): void {
    // Stop pushing the logged-out user's messages to this device first - needs the token, so before logging out.
    Promise.race([this.pushService.detachDevice(), new Promise<void>(resolve => setTimeout(resolve, 2000))])
      .finally(() => {
        this.authService.logout();
        this.router.navigate(['/']);
        this.snackBar.open('Logged out.', 'Ok', {duration: 4000});
      });
  }

  openMfaSettingsDialog(): void {
    import('./auth/mfa-settings-dialog/mfa-settings-dialog.component').then(({ MfaSettingsDialogComponent }) => {
      this.dialog.open(MfaSettingsDialogComponent, {
        width: '480px',
        maxWidth: '95vw',
        panelClass: 'mobile-fullscreen-dialog',
        position: TOP_DIALOG_POSITION
      });
    });
  }

  /** During the grace period: reminds staff in a mandatory role, once per page load, to set up the second factor. */
  private remindAboutMfaSetup(): void {
    if (this.mfaReminderShown || !this.canUseMfa) {
      return;
    }
    this.mfaReminderShown = true;
    this.authService.getMfaStatus().subscribe({
      next: status => {
        if (status.enabled || !status.requiredByRole) {
          return;
        }
        const deadline = status.enforcedFrom ? ` ab dem ${status.enforcedFrom.split('-').reverse().join('.')}` : ' künftig';
        this.snackBar.open(
          `Bitte richte die Zwei-Faktor-Authentisierung ein (Pflicht${deadline}): Schloss-Symbol in der Kopfzeile.`,
          'Ok', {duration: 15000});
      },
      error: () => {
        // purely a reminder, nothing to do if the status can't be read
      }
    });
  }

  openPushSettingsDialog(): void {
    import('./push/push-settings-dialog/push-settings-dialog.component').then(({ PushSettingsDialogComponent }) => {
      this.dialog.open(PushSettingsDialogComponent, {
        width: '480px',
        maxWidth: '95vw',
        panelClass: 'mobile-fullscreen-dialog',
        position: TOP_DIALOG_POSITION
      });
    });
  }

  /** A tapped push notification brings the user to their messages: an already open tab gets a message from the
   * service worker, a freshly opened one the ?messages=1 parameter. Only staff have the message list in the
   * toolbar; everybody else just lands in the app. */
  private setupMessageDeepLinks(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data?.type === 'cba-open-messages' && this.isStaff) {
          this.openStaffMessagesDialog();
        }
      });
    }
    const params = new URLSearchParams(window.location.search);
    // Link of the "uMap-Icon ohne Zuordnung" message: opens the Icon-Zuordnung right at that mapping.
    const routeIconMapping = params.get('routeIconMapping');
    if (routeIconMapping) {
      params.delete('routeIconMapping');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
      if (this.canManageRouteIcons) {
        setTimeout(() => this.openRouteIconMappingsDialog(routeIconMapping));
      }
    }
    if (params.has('messages')) {
      params.delete('messages');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
      if (this.isStaff) {
        setTimeout(() => this.openStaffMessagesDialog());
      }
    }
  }

  private handleSessionExpired(): void {
    if (this.sessionExpiredDialogOpen) {
      return;
    }
    this.sessionExpiredDialogOpen = true;

    this.snackBar.open('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.', 'Ok', {duration: 6000});

    const dialogRef = this.dialog.open(LoginDialogComponent, {
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(loggedIn => {
      this.sessionExpiredDialogOpen = false;
      if (loggedIn) {
        this.snackBar.open('Logged in successfully.', 'Ok', {duration: 4000});
        this.navigateHomeIfSelfService('session-expired');
      }
    });
  }
}
